/**
 * Linear-style product mock — a small slice of the actual EchoCRM UI:
 * left rail with workspaces, lead list with status dots, detail pane.
 * On mobile: shows the card-style lead sheet that matches the real app.
 * No glassmorphism, no tilt, no glow. Just a clean window chrome.
 */
const leads = [
  { name: "Sarah Chen", co: "Acme Corp", val: "$50k", dot: "bg-primary", active: true, priority: "HIGH", status: "DEMO", days: 3, next: "Follow-up Tue" },
  { name: "Mike Lawson", co: "BuildRight", val: "$120k", dot: "bg-primary", priority: "HIGH", status: "NEGOT", days: 5, next: "Send proposal" },
  { name: "Northwind Interiors", co: "Showroom · District 4", val: "$28k", dot: "bg-muted-foreground/50", priority: "MED", status: "NEW", days: 7, next: "Site visit" },
  { name: "Priya Shah", co: "BrightSign", val: "$74k", dot: "bg-primary", priority: "HIGH", status: "DEMO", days: 2, next: "Demo meeting" },
  { name: "Diego Alvarez", co: "Nova Realty", val: "$200k", dot: "bg-muted-foreground/50", priority: "LOW", status: "WARM", days: 12, next: "Check in" },
  { name: "Hannah Webb", co: "PinePoint", val: "$15k", dot: "bg-primary", priority: "MED", status: "NEW", days: 4, next: "Intro call" },
];

export function ProductMock({ className = "" }: { className?: string }) {
  return (
    <div className={`relative rounded-lg border border-border bg-card overflow-hidden shadow-sm ${className}`}>
      {/* Window chrome */}
      <div className="hairline px-3 py-2 flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-muted" />
        <span className="h-2.5 w-2.5 rounded-full bg-muted" />
        <span className="h-2.5 w-2.5 rounded-full bg-muted" />
        <span className="ml-3 text-[11px] text-muted-foreground font-mono-accent">echocrm.app</span>
      </div>

      {/* Mobile: card-style lead sheet matching the real mobile app */}
      <div className="sm:hidden hairline-t bg-background">
        <div className="px-4 py-3 hairline flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="text-[13px] font-semibold">Hot leads</span>
          </div>
          <span className="text-[10px] text-muted-foreground font-mono-accent">24 records</span>
        </div>
        <div className="space-y-2 p-3">
          {leads.slice(0, 5).map((l) => (
            <div
              key={l.name}
              className={`relative rounded-xl border bg-card p-3 ${l.active ? "border-primary/40 ring-1 ring-primary/20" : "border-border"}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="truncate font-semibold text-[13px] text-foreground">{l.name}</h3>
                    <span className="shrink-0 rounded border border-border px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
                      {l.priority}
                    </span>
                    <span className="shrink-0 rounded border border-primary/40 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider text-primary">
                      {l.status}
                    </span>
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground mt-0.5">{l.co} · {l.val}</p>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 border-t border-border/60 pt-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">{l.days}d left</span>
                  <p className="truncate text-[11px] text-muted-foreground">{l.next}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop: 3-column window */}
      <div className="hidden sm:grid sm:grid-cols-[180px_1fr_220px] min-h-[360px]">
        {/* Sidebar */}
        <aside className="hairline-t p-3 border-r border-border">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-mono-accent">Workspaces</p>
          <ul className="space-y-1 text-[12px]">
            <li className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-accent text-foreground"><span className="h-1.5 w-1.5 rounded-full bg-primary" />Sales Team</li>
            <li className="flex items-center gap-2 px-2 py-1.5 rounded-md text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />Personal</li>
            <li className="flex items-center gap-2 px-2 py-1.5 rounded-md text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />Real Estate</li>
          </ul>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-5 mb-2 font-mono-accent">Pipeline</p>
          <ul className="space-y-1 text-[12px] text-muted-foreground">
            <li className="px-2 py-1.5">Hot leads <span className="float-right tabular-nums">24</span></li>
            <li className="px-2 py-1.5">Cold leads <span className="float-right tabular-nums">112</span></li>
            <li className="px-2 py-1.5">Refresher <span className="float-right tabular-nums">7</span></li>
          </ul>
        </aside>

        {/* Lead list */}
        <main className="hairline-t">
          <div className="px-4 py-2.5 hairline flex items-center justify-between">
            <span className="text-[12px] font-medium">Hot leads</span>
            <span className="text-[10px] text-muted-foreground font-mono-accent">24 records</span>
          </div>
          <ul className="text-[12px]">
            {leads.map((r) => (
              <li key={r.name} className={`px-4 py-2.5 hairline flex items-center gap-3 ${r.active ? "bg-accent/60" : ""}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${r.dot} flex-shrink-0`} />
                <span className="font-medium truncate flex-1">{r.name}</span>
                <span className="text-muted-foreground truncate max-w-[120px]">{r.co}</span>
                <span className="tabular-nums text-foreground/80">{r.val}</span>
              </li>
            ))}
          </ul>
        </main>

        {/* Detail */}
        <aside className="hairline-t border-l border-border p-4">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-accent mb-3">Selected</p>
          <h4 className="font-display text-[15px] font-semibold mb-1">Sarah Chen</h4>
          <p className="text-[12px] text-muted-foreground mb-4">Acme Corp · $50,000</p>
          <div className="space-y-2.5 text-[12px]">
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-primary" />Demo booked</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Next step</span><span>Follow-up Tue</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Source</span><span>Referral</span></div>
          </div>
          <div className="mt-5 hairline-t pt-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-accent mb-2">Timeline</p>
            {["Initial call", "Send proposal", "Demo meeting"].map((t, i) => (
              <div key={t} className="flex items-center gap-2 text-[12px] py-1">
                <span className="h-3 w-3 rounded-full border border-border flex items-center justify-center">
                  {i < 2 && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                </span>
                <span className={i === 2 ? "text-muted-foreground" : ""}>{t}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}